import { useState } from "react";
import {
  BiChevronLeft,
  BiChevronRight,
  BiLogOut,
  BiMenu,
} from "react-icons/bi";
import { getStoredUser } from "../../services/userService";
import { colors } from "../../styles/theme/colors";
import { typography } from "../../styles/theme/typography";
import {
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Typography,
  Fade,
} from "@mui/material";

const Header = ({ sidebarOpen, toggleSidebar, onMobileMenuClick }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const user = getStoredUser();
  
  // Default values if user is null
  const defaultUser = {
    role: "Admin",
    fullName: "Admin Dixit",
    profilePicture: "https://randomuser.me/api/portraits/men/32.jpg"
  };

  const getProfilePic = () => {
    if (!user) return defaultUser.profilePicture;
    if (typeof user.profilePicture === "string") return user.profilePicture;
    if (user.profilePicture?.url) return user.profilePicture.url;
    return defaultUser.profilePicture;
  };

  const handleDropdownOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setDropdownOpen(true);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
    setDropdownOpen(false);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: colors.background.paper,
        borderBottom: `1px solid ${colors.border.light}`,
        boxShadow: "0 2px 8px 0 rgba(31,41,55,0.04)",
        zIndex: 100,
        width: "100%",
        maxWidth: "100vw",
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 56, md: 72 },
          px: { xs: 1, md: 3 },
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "100vw",
          flexWrap: "nowrap",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {/* Left Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            gap: 2,
            minWidth: 0,
            width: "100%",
            maxWidth: "100vw",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* Mobile Menu Button */}
          <IconButton
            onClick={onMobileMenuClick}
            sx={{
              color: colors.text.secondary,
              display: { xs: "inline-flex", md: "none" },
              fontSize: 28,
              p: 1,
            }}
          >
            <BiMenu size={28} />
          </IconButton>
          {/* Sidebar Arrow Button */}
          <IconButton
            onClick={toggleSidebar}
            sx={{
              color: colors.text.secondary,
              display: { xs: "none", md: "inline-flex" },
              fontSize: 24,
              p: 1,
            }}
          >
            {sidebarOpen ? (
              <BiChevronLeft size={24} />
            ) : (
              <BiChevronRight size={24} />
            )}
          </IconButton>
          {/* Greeting */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "column" },
              minWidth: 0,
              alignItems: { xs: "center", md: "flex-start" },
              gap: { xs: 1, md: 0 },
              width: "100%",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Typography
              className="poppins-font"
              sx={{
                fontWeight: typography.fontWeight.semibold,
                fontSize: { xs: "1rem", md: "1.1rem" },
                color: colors.text.primary,
                display: "flex",
                alignItems: "center",
                gap: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
                minWidth: 0,
                flexShrink: 1,
              }}
            >
              Hello {user?.role || defaultUser.role} {user?.fullName || defaultUser.fullName}
              <Box component="span" sx={{ fontSize: "1.3rem", ml: 1 }}>
                👋
              </Box>
            </Typography>
            <Typography
              className="poppins-font"
              sx={{
                color: colors.text.secondary,
                fontSize: "0.95rem",
                fontWeight: typography.fontWeight.regular,
                opacity: 0.85,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: { xs: "none", sm: "block", md: "block" },
                width: "100%",
                maxWidth: "100vw",
              }}
            >
              Welcome back! Hope you have a productive day.
            </Typography>
          </Box>
        </Box>
        {/* Right Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            ml: 2,
            flexShrink: 0,
          }}
        >
          <Box>
            <IconButton
              onClick={handleDropdownOpen}
              sx={{
                p: 0,
                borderRadius: "50%",
                border: `2px solid ${colors.primary.main}`,
                boxShadow: "0 2px 8px 0 rgba(31,41,55,0.08)",
              }}
            >
              <Avatar
                src={getProfilePic()}
                alt="User"
                sx={{
                  width: 44,
                  height: 44,
                }}
              />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={dropdownOpen}
              onClose={handleDropdownClose}
              TransitionComponent={Fade}
              PaperProps={{
                sx: {
                  minWidth: 260,
                  borderRadius: 3,
                  mt: 1,
                  boxShadow: "0 8px 32px 0 rgba(31,41,55,0.12)",
                  border: `1px solid ${colors.border.light}`,
                  overflow: "hidden",
                },
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderBottom: `1px solid ${colors.border.light}`,
                  background: colors.background.default,
                }}
              >
                <Avatar
                  src={getProfilePic()}
                  alt="User"
                  sx={{ width: 40, height: 40 }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text.primary,
                    }}
                  >
                    {user?.fullName || "John Doe"}
                  </Typography>
                  <Typography
                    sx={{ color: colors.text.secondary, fontSize: "0.92rem" }}
                  >
                    {user?.role || "UI/UX Designer"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ py: 1 }}>
                  <>
                    <DropdownItem
                      icon={<BiLogOut />}
                      label="Logout"
                      color={colors.error.main}
                    />
                  </>
              </Box>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

function DropdownItem({ icon, label, color }) {
  return (
    <MenuItem
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        color: color || "#23272e",
        fontWeight: 500,
        fontSize: "1rem",
        py: 1.2,
        transition: "background 0.15s",
        "&:hover": { background: "#f3f4f6" },
      }}
    >
      <Box sx={{ fontSize: 20 }}>{icon}</Box>
      {label}
    </MenuItem>
  );
}

export default Header;
